import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Badge,
  Checkbox,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useRestoreBackup,
  useInstanceConfig,
  type Backup,
  type BackupRestoreMode,
} from '@/api/opennebula';
import { FLAVOR_TYPES } from '../instances/instance-create/shared';

const backupSizeGB = (mb?: string | number) => {
  const n = Number(mb);
  return Number.isFinite(n) && n > 0 ? `${Math.round((n / 1024) * 10) / 10} GB` : '—';
};
const backupDate = (epoch?: string | number) => {
  const ms = Number(epoch) * 1000;
  return Number.isFinite(ms) && ms > 0 ? new Date(ms).toLocaleString() : '—';
};

/**
 * Restore a backup. Two modes mirror the legacy wizard: "Clone to new instance"
 * (creates a fresh instance from the backup — requires a name + flavor) and "Restore
 * in-place" (replaces the system volume of the original instance).
 */
export function BackupRestoreDialog({
  backup,
  onClose,
}: {
  backup: Backup | null;
  onClose: () => void;
}) {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: config } = useInstanceConfig(serviceId);
  const flavors = config?.flavors ?? [];
  const networks = config?.networks ?? [];

  const [mode, setMode] = useState<BackupRestoreMode>('clone');
  const [name, setName] = useState('');
  const [flavorType, setFlavorType] = useState<string>('standard');
  const [flavor, setFlavor] = useState('');
  const visibleFlavors = flavors.filter((f) => f.type === flavorType);
  // The backend requires ≥1 network for a clone — default-select the first so the
  // common case works, while still letting the user attach more or change it.
  const [netIds, setNetIds] = useState<string[]>([]);

  useEffect(() => {
    if (!!backup && netIds.length === 0 && networks.length > 0) {
      setNetIds([String(networks[0].id)]);
    }
  }, [backup, networks, netIds.length]);

  const restore = useRestoreBackup(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Restore requested' });
      onClose();
      setName('');
      setFlavorType('standard');
      setFlavor('');
      setNetIds([]);
      setMode('clone');
    },
    onError: () => show({ tone: 'danger', title: 'Restore failed' }),
  });

  const toggleNet = (id: string, on: boolean) =>
    setNetIds((prev) => (on ? [...new Set([...prev, id])] : prev.filter((n) => n !== id)));

  // Clone requires a name, a flavor, AND at least one network — the backend
  // instantiates a fresh instance and rejects an empty network list.
  const cloneValid =
    mode === 'clone' ? name.trim().length > 0 && flavor.length > 0 && netIds.length > 0 : true;
  const inPlaceValid = mode === 'restore' ? !!backup?.attached?.instance_id : true;
  const valid = cloneValid && inPlaceValid;

  const submit = () => {
    if (!backup) return;
    const payload =
      mode === 'clone'
        ? {
            type: 'clone' as const,
            instance: { name: name.trim(), flavor, networks: netIds.map((id) => ({ id })) },
          }
        : { type: 'restore' as const };
    restore.mutate({ backupId: backup.id, payload });
  };

  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restore backup — {backup?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {/* Backup info card */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 'var(--vn-space-2)',
                padding: 'var(--vn-space-3)',
                borderRadius: 'var(--vn-radius-md)',
                background: 'var(--vn-bg-muted, var(--vn-bg-surface))',
                border: '1px solid var(--vn-border-default)',
              }}
            >
              <InfoCell label="Backup" value={backup?.name || `backup-${backup?.id ?? ''}`} />
              <InfoCell label="Type" value={backup?.backup_increments == null ? 'Full' : 'Incremental'} />
              <InfoCell label="Size" value={backupSizeGB(backup?.size)} />
              <InfoCell label="Created" value={backupDate(backup?.created_at)} />
            </div>

            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <SegmentedControl
                aria-label="Restore mode"
                value={mode}
                onValueChange={(v) => setMode(v as BackupRestoreMode)}
                items={[
                  { value: 'clone', label: 'Clone to new instance' },
                  { value: 'restore', label: 'Restore back to current' },
                ]}
              />
              <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                {mode === 'clone' ? (
                  <>
                    <Badge tone="success" appearance="subtle" size="sm">Recommended</Badge>
                    Restored to new images, creating a new instance so you can continue without conflicts.
                  </>
                ) : (
                  'Restored to a new volume, which then replaces the current system volume of your instance.'
                )}
              </p>
            </div>

            {mode === 'clone' ? (
              <>
                <div>
                  <Label htmlFor="restore-name">New instance name</Label>
                  <Input id="restore-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="restored-instance" />
                </div>
                <div>
                  <Label htmlFor="restore-flavor" required>Resource flavor</Label>
                  {/* Type filter (Standard / Processor / Memory) narrows the flavor list,
                      mirroring the instance-create wizard. Changing type clears the flavor. */}
                  <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                    <Select value={flavorType} onValueChange={(v) => { setFlavorType(v); setFlavor(''); }}>
                      <SelectTrigger style={{ maxWidth: 200, flexShrink: 0 }} aria-label="Flavor type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FLAVOR_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={flavor} onValueChange={setFlavor}>
                      <SelectTrigger id="restore-flavor" style={{ flex: 1, minWidth: 0 }}>
                        <SelectValue placeholder="Choose an instance flavor" />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleFlavors.length === 0 ? (
                          <SelectItem value="__none" disabled>No flavors in this category</SelectItem>
                        ) : (
                          visibleFlavors.map((f) => (
                            <SelectItem key={f.id ?? f.name} value={f.name}>
                              {f.name}{f.cores ? ` — ${f.cores} vCPU` : ''}{f.memory ? `, ${f.memory} GB` : ''}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label required>Networks</Label>
                  {networks.length === 0 ? (
                    <p style={{ margin: 'var(--vn-space-1) 0 0', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                      No networks available in this service.
                    </p>
                  ) : (
                    <div
                      style={{
                        marginTop: 'var(--vn-space-1)',
                        display: 'grid',
                        gap: 'var(--vn-space-2)',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      }}
                    >
                      {networks.map((n) => {
                        const id = String(n.id);
                        const selected = netIds.includes(id);
                        const isPublic = /public/i.test(n.type ?? n.name ?? '');
                        return (
                          <label
                            key={id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--vn-space-2)',
                              padding: 'var(--vn-space-2) var(--vn-space-3)',
                              borderRadius: 'var(--vn-radius-md)',
                              border: `1px solid ${selected ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                              background: selected ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                              cursor: 'pointer',
                              minWidth: 0,
                            }}
                          >
                            <Checkbox checked={selected} onCheckedChange={(c) => toggleNet(id, c === true)} />
                            <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
                              <span
                                style={{
                                  fontSize: 'var(--vn-font-size-sm)',
                                  fontWeight: 'var(--vn-font-weight-medium)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {n.name}
                              </span>
                              <Badge tone={isPublic ? 'info' : 'neutral'} appearance="subtle" size="sm">
                                {isPublic ? 'Public' : 'Private'}
                              </Badge>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : backup?.attached?.instance_id ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
                  gap: 'var(--vn-space-3)',
                  padding: 'var(--vn-space-3)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: 'var(--vn-warning-subtle)',
                  border: '1px solid var(--vn-warning-border)',
                }}
              >
                <InfoCell label="Instance name" value={backup.attached.name} />
                <InfoCell label="Instance ID" value={backup.attached.instance_id} />
                <InfoCell label="Instance disk slots" value={String(backup.attached.backup_disks ?? '—')} />
              </div>
            ) : (
              <p
                style={{
                  margin: 0,
                  padding: 'var(--vn-space-3)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: 'var(--vn-warning-subtle)',
                  border: '1px solid var(--vn-warning-border)',
                  fontSize: 'var(--vn-font-size-sm)',
                }}
              >
                The reference instance for this backup is no longer valid or has been deleted. You can
                either delete this backup or restore it to a new instance.
              </p>
            )}

            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Note: Restoration will take a few minutes. The exact time depends on the size of your backup
              and, at times, on the network conditions of our backup infrastructure.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!valid || restore.isPending} onClick={submit}>
            {mode === 'clone' ? 'Clone & restore' : 'Restore in-place'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoCell({ label, value }: { label: string; value?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-bold)', color: 'var(--vn-fg-muted)' }}>{label}</div>
      <div style={{ fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value || '—'}</div>
    </div>
  );
}
